import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { PRIMARY_TASKS, type PrimaryTask, type TaskBreakdownEntry } from '../../api/types';

interface Props {
  tasks: TaskBreakdownEntry[];
  /** Sessions that don't have a v5 classification yet */
  unclassified: number;
  loading?: boolean;
}

const TASK_COLORS: Record<PrimaryTask, string> = {
  page_migration: '#6366f1',     // indigo 500
  refinement: '#ec4899',          // pink 500 (replaces muddy purple)
  skill_authoring: '#10b981',     // emerald 500
  content_generation: '#f59e0b',  // amber 500
  other: '#71717a',               // zinc 500
};

const TASK_LABELS: Record<PrimaryTask, string> = {
  page_migration: 'Page migration',
  refinement: 'Refinement',
  skill_authoring: 'Skill authoring',
  content_generation: 'Content generation',
  other: 'Other',
};

const UNCLASSIFIED_COLOR = '#d4d4dc'; // zinc 200 — keeps "absent" feel without being heavy

export function TaskBreakdownDonut({ tasks, unclassified, loading }: Props): JSX.Element {
  const data = PRIMARY_TASKS.map((task) => {
    const entry = tasks.find((t) => t.primaryTask === task);
    return {
      name: TASK_LABELS[task],
      value: entry?.sessions ?? 0,
      itemStyle: { color: TASK_COLORS[task] },
    };
  }).filter((d) => d.value > 0);

  if (unclassified > 0) {
    data.push({
      name: 'Unclassified',
      value: unclassified,
      itemStyle: { color: UNCLASSIFIED_COLOR },
    });
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        const value = p.value as number;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
        return `<strong>${p.name}</strong><br/>${value.toLocaleString('en-US')} sessions (${pct}%)`;
      },
    },
    legend: {
      orient: 'vertical',
      right: 16,
      top: 'middle',
      itemWidth: 12,
      itemHeight: 12,
      icon: 'roundRect',
      formatter: (name) => {
        const entry = data.find((d) => d.name === name);
        if (!entry) return name;
        const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
        return `${name}  ${entry.value.toLocaleString('en-US')} (${pct}%)`;
      },
      textStyle: { fontSize: 12 },
    },
    series: [
      {
        type: 'pie',
        radius: ['52%', '78%'],
        center: ['28%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 4, borderColor: 'transparent', borderWidth: 2 },
        label: {
          show: true,
          position: 'center',
          formatter: () => `{n|${total.toLocaleString('en-US')}}\n{l|sessions}`,
          rich: {
            n: { fontSize: 28, fontWeight: 'bold', color: 'inherit' },
            l: { fontSize: 12, color: '#888', padding: [6, 0, 0, 0] },
          },
        },
        labelLine: { show: false },
        data,
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 280, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
